import React, { useRef, useState, useEffect, useContext } from 'react';
import { Box, Typography, Button, Paper, Divider, TextField, List, ListItem, ListItemText, IconButton, CircularProgress, Alert, Avatar } from '@mui/material';
import { 
  Delete, 
  UploadFile, 
  Brush, 
  Chat,
  Send,
  Image,
  Visibility
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { collection, addDoc, deleteDoc, onSnapshot, doc } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { uploadFileAndGetURL } from '../utils/documentService';
import { AuthContext } from '../contexts/AuthContext';

interface BoardImage {
  id: string;
  url: string;
  name: string;
  type: string;
  uploadedAt: number;
  userId: string;
  userName: string;
}

const SketchDrawPhase: React.FC = () => {
  const { teamId, sprintId } = useParams<{ teamId: string; sprintId: string }>();
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;

  // Image upload
  const [images, setImages] = useState<BoardImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  // Comments
  const [comments, setComments] = useState<any[]>([]); // Changed to any[] to accommodate userId and userName
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  // Firestore listeners
  useEffect(() => {
    if (!teamId || !sprintId) return;
    // Images
    const unsubImages = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/sketchDraw_images`), snap => {
      setImages(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as BoardImage)));
    });
    // Comments
    const unsubComments = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/sketchDraw_comments`), snap => {
      setComments(snap.docs.map(doc => doc.data() as any));
    });
    return () => {
      unsubImages();
      unsubComments();
    };
  }, [teamId, sprintId]);

  // Image upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && teamId && sprintId) {
      setImageLoading(true);
      setImageError(null);
      try {
        const filePath = `teams/${teamId}/designSprints/${sprintId}/sketchDraw/${Date.now()}_${file.name}`;
        const url = await uploadFileAndGetURL(file, filePath);
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/sketchDraw_images`), {
          url,
          name: file.name,
          type: file.type,
          uploadedAt: Date.now(),
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (err) {
        setImageError('Failed to upload image.');
      }
      setImageLoading(false);
    }
  };

  const handleDeleteImage = async (id: string) => {
    if (!teamId || !sprintId) return;
    try {
      await deleteDoc(doc(firestore, `teams/${teamId}/designSprints/${sprintId}/sketchDraw_images`, id));
    } catch (err) {
      setImageError('Failed to delete image.');
    }
  };

  // Comments
  const handleAddComment = async () => {
    if (newComment.trim() && teamId && sprintId) {
      setCommentLoading(true);
      setCommentError(null);
      try {
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/sketchDraw_comments`), {
          text: newComment.trim(),
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        setNewComment('');
      } catch (err) {
        setCommentError('Failed to add comment.');
      }
      setCommentLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Whiteboard Images Section */}
      <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 border border-teal-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center">
            <Brush className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Whiteboard Images</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Upload sketches, drawings, and visual ideas</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadFile />}
            disabled={imageLoading}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #14b8a6 0%, #0891b2 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #0d9488 0%, #0e7490 100%) !important',
                color: '#ffffff !important'
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#6b7280'
              }
            }}
          >
            Upload Image
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageUpload}
            />
          </Button>
        </div>

        {imageLoading && (
          <div className="flex items-center gap-2 text-teal-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Uploading image...</span>
          </div>
        )}
        
        {imageError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {imageError}
          </Alert>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {images.map(img => (
            <div key={img.id} className="relative group">
              <div className="bg-white rounded-xl p-3 border border-gray-200 hover:shadow-md transition-all duration-200">
                <div className="relative">
                  <img 
                    src={img.url} 
                    alt={img.name} 
                    className="w-full h-24 object-cover rounded-lg border border-gray-200"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-1">
                      <IconButton
                        size="small"
                        sx={{ 
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                        }}
                        href={img.url}
                        target="_blank"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteImage(img.id)}
                        disabled={imageLoading}
                        sx={{ 
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          color: '#ef4444',
                          '&:hover': { 
                            backgroundColor: 'rgba(255,255,255,1)',
                            color: '#dc2626'
                          }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-xs text-gray-600 font-['Inter'] truncate" title={img.name}>
                    {img.name}
                  </p>
                  <p className="text-xs text-gray-500 font-['Inter']">
                    by {img.userName || 'Unknown'}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {images.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Image className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-['Inter']">No images uploaded yet. Start by uploading your sketches!</p>
          </div>
        )}
      </div>

      {/* Comments Section */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
            <Chat className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Team Discussion</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Share thoughts and collaborate</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="Write a comment..."
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            size="small"
            fullWidth
            disabled={commentLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="Share your thoughts..."
            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
          />
          <Button
            variant="contained"
            onClick={handleAddComment}
            disabled={commentLoading || !newComment.trim()}
            startIcon={<Send />}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%) !important',
                color: '#ffffff !important'
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#6b7280'
              }
            }}
          >
            Send
          </Button>
        </div>

        {commentLoading && (
          <div className="flex items-center gap-2 text-purple-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Sending comment...</span>
          </div>
        )}
        
        {commentError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {commentError}
          </Alert>
        )}

        <div className="space-y-3">
          {comments.map((comment, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex items-start gap-3">
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'purple.500' }}>
                  {(comment.userName || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <div className="flex-1">
                  <p className="text-gray-900 font-['Inter']">{comment.text}</p>
                  <p className="text-sm text-gray-500 font-['Inter'] mt-1">by {comment.userName || 'Unknown'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {comments.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Chat className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-['Inter']">No comments yet. Start the discussion!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SketchDrawPhase; 